import React, { FC, useState, Fragment, useEffect } from 'react';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { deepEqual } from 'fast-equals';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add,
  Close,
  Assignment,
  FormatListBulleted,
  Edit,
  Save,
  Delete,
  DragIndicator,
  Visibility
} from '@mui/icons-material';
// Components
import { Modal, Loader, ImageUpload, ImageModal, SMBSelect } from '../../../components';
import {
  Box,
  Card,
  Fade,
  Button,
  CardContent,
  CardHeader,
  TextField,
  Grid,
  Paper,
  Typography,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  ButtonGroup,
  useMediaQuery
} from '@mui/material';
import { TaskTemplateModalMobile } from './TaskTemplateModalMobile';
import { reorder } from '../../../helpers';
import {
  IStartSheetTemplateDetail,
  IStartSheetTemplateCategory,
  IDomainUser,
  ISheetTask,
  IStartSheetTemplate
} from '../../../models';
import {
  getStartSheetTemplateCategories,
  getDomainUsers,
  getStartSheetTemplates,
  updateStartSheetTemplate,
  createStartSheetTemplate,
  getStartSheetTemplateDetail
} from '../../../fetch';

interface ITemplateModal {
  type: string;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  currentTemplate: IStartSheetTemplateDetail;
}

const NeighborhoodTemplateSchema = Yup.object().shape({
  name: Yup.string().max(255, 'Max 255 characters').required('Required'),
  tasks: Yup.array().of(
    Yup.object().shape({
      text: Yup.string(),
      categories: Yup.array().nullable(),
      assignedTo: Yup.string().nullable(),
      subscribers: Yup.array().nullable(),
      images: Yup.array().nullable()
    })
  )
});

interface ISortableList {
  isMobile: boolean;
  setCurrentTemplateTaskIndex: (val: number | null) => void;
  tasks: ISheetTask[];
  setFieldValue: (key: string, val: any) => void;
}

interface ISortableItem {
  isMobile: boolean;
  setCurrentTemplateTaskIndex: (val: number | null) => void;
  tasks: ISheetTask[];
  setFieldValue: (key: string, val: any) => void;
  task: ISheetTask;
  itemIndex: number;
}

const DragHandle = SortableHandle(() => {
  const classes = useStyles();
  return <DragIndicator className={classes.dragIcon} />;
});

const SortableList = SortableContainer(
  ({ isMobile, tasks, setCurrentTemplateTaskIndex, setFieldValue }: ISortableList) => {
    return (
      <div>
        {tasks &&
          tasks.length > 0 &&
          tasks.map((item, index) => (
            <SortableItem
              key={item.taskId}
              isMobile={isMobile}
              itemIndex={index}
              setFieldValue={setFieldValue}
              setCurrentTemplateTaskIndex={setCurrentTemplateTaskIndex}
              index={index}
              task={item}
              tasks={tasks}
            />
          ))}
      </div>
    );
  }
);
const SortableItem = SortableElement(
  ({
    isMobile,
    setFieldValue,
    itemIndex,
    tasks,
    task,
    setCurrentTemplateTaskIndex
  }: ISortableItem) => {
    const classes = useStyles();

    const [selectedImgSource, setSelectedImgSource] = useState('');

    return isMobile ? (
      <TaskTemplateModalMobile
        original={task}
        handleEdit={() => {
          setCurrentTemplateTaskIndex(itemIndex);
        }}
        handleDelete={val => {
          const result = window.confirm('Are you sure you want to delete this task?');
          if (result) {
            const newValues = [...tasks];
            const filtered = newValues.filter(task => task.text !== val.text);
            setFieldValue(`tasks`, filtered);
          }
        }}
      />
    ) : (
      <>
        <Card className={classes.taskCard}>
          <CardContent className={classes.taskCardContent}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography className={classes.bold}>Order</Typography>
                <Typography className={classes.taskValue}>{itemIndex + 1}</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography className={classes.bold}>Text</Typography>
                <Typography className={classes.taskValue}>{task.text || '--'}</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography className={classes.bold}>Categories</Typography>
                <Typography className={classes.taskValue}>
                  {task.categories && task.categories.length > 0
                    ? task.categories.map(cat => cat.name).join(', ')
                    : '--'}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography className={classes.bold}>Assigned To</Typography>
                <Typography className={classes.taskValue}>{task.assignedTo || '--'}</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography className={classes.bold}>Subscribers</Typography>
                <Typography className={classes.taskValue}>
                  {task.subscribers && task.subscribers.length > 0
                    ? task.subscribers.join(', ')
                    : '--'}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography className={classes.bold}>Images</Typography>
                <Typography className={classes.taskValue}>
                  {task.images && task.images.length > 0
                    ? task.images.map((image, index) => {
                        return (
                          <Button
                            startIcon={<Visibility />}
                            variant="text"
                            color="secondary"
                            key={`${image}`}
                            onClick={() => setSelectedImgSource(image)}
                          >
                            image.png{index > 0 ? ` - ${index + 1}` : ''}
                          </Button>
                        );
                      })
                    : '--'}
                </Typography>
              </Grid>
              <DragHandle />
              <Grid item xs={3}>
                <div className={classes.actionButtons}>
                  <Button
                    color="primary"
                    className={classes.editButton}
                    startIcon={<Edit />}
                    onClick={async () => {
                      setCurrentTemplateTaskIndex(itemIndex);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    id="delete"
                    className={classes.deleteButton}
                    startIcon={<Delete />}
                    onClick={async () => {
                      const result = window.confirm('Are you sure you want to delete this task?');
                      if (result) {
                        const newValues = [...tasks];
                        const filtered = newValues.filter(t => t.text !== task.text);
                        setFieldValue(`tasks`, filtered);
                      }
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        <ImageModal
          isOpen={!!selectedImgSource}
          onClose={() => setSelectedImgSource('')}
          source={selectedImgSource}
        />
      </>
    );
  }
);

export const TemplateModal: FC<ITemplateModal> = ({
  type,
  open,
  onClose,
  onSave,
  currentTemplate
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [currentTemplateTaskIndex, setCurrentTemplateTaskIndex] = useState<number | null>(null);
  const [isLoadingCategories, setLoadingCategories] = useState(false);
  const [isLoadingUsers, setLoadingUsers] = useState(false);
  const [categories, setCategories] = useState<IStartSheetTemplateCategory[]>([]);
  const [users, setUsers] = useState<IDomainUser[]>([]);
  const [loadingStartTemplates, setLoadingStartTemplates] = useState(false);
  const [startTemplates, setStartTemplates] = useState<IStartSheetTemplate[] | null>(null);
  const [copyTaskSelectedTemplate, setCopyTaskSelectedTemplate] = useState<string>('');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [templateIsEmpty, setTemplateIsEmpty] = useState(false);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const res = await getStartSheetTemplateCategories(type);
      setCategories(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingCategories(false);
    }
  };
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await getDomainUsers();
      setUsers(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoadingStartTemplates(true);
      const res = await getStartSheetTemplates(type, {
        page: 1,
        perPage: 1000,
        sortBy: 'Name'
      });
      setStartTemplates(res.records);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingStartTemplates(false);
    }
  };

  const fetchDetail = async () => {
    setLoadingDetail(true);
    try {
      const res = await getStartSheetTemplateDetail(Number(copyTaskSelectedTemplate));
      setTemplateDetail(res);

      if (res.tasks.length < 1) {
        setTemplateIsEmpty(true);
      } else {
        setTemplateIsEmpty(false);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (copyTaskSelectedTemplate) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyTaskSelectedTemplate]);

  useEffect(() => {
    if (templateIsEmpty) {
      enqueueSnackbar(`There are no tasks to copy`, {
        variant: 'warning'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateIsEmpty]);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: currentTemplate ? currentTemplate.name : '',
          tasks: currentTemplate ? currentTemplate.tasks || [] : []
        }}
        validationSchema={NeighborhoodTemplateSchema}
        onSubmit={async (values, actions) => {
          try {
            const data = {
              ...values,
              tasks: values.tasks.map(task => ({
                assignedTo: task.assignedTo,
                images: task.images,
                subscribers: task.subscribers,
                taskId: task.taskId || null,
                text: task.text,
                categoryIds: task.categories.map(cat => cat.startSheetTemplateCategoryId)
              }))
            };
            currentTemplate
              ? await updateStartSheetTemplate(currentTemplate.startSheetTemplateId, data)
              : await createStartSheetTemplate(type, data);

            enqueueSnackbar(`Sheet template ${currentTemplate ? 'updated' : 'created'}!`, {
              variant: 'success'
            });
            onClose();
            onSave();
            actions.resetForm();

            if (type === 'ModelHome') {
              fetchTemplates();
            }
          } catch (error) {
            enqueueSnackbar(
              error?.Detail ?? `Error, sheet template update failed, please try again.`,
              {
                variant: 'error'
              }
            );
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          const onSortEnd = ({ oldIndex, newIndex }) => {
            const items = reorder(values.tasks, oldIndex, newIndex);
            setFieldValue('tasks', items);
          };
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    setCopyTaskSelectedTemplate('');
                    setTemplateIsEmpty(false);
                    setCurrentTemplateTaskIndex(null);
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  resetForm();
                  setCopyTaskSelectedTemplate('');
                  setCurrentTemplateTaskIndex(null);
                  setTemplateIsEmpty(false);
                  onClose();
                }
              }}
              maxWidth="lg"
              title={`${currentTemplate ? 'Edit' : 'Add'} Start Sheet Template`}
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <div className={classes.content}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Card elevation={0} variant="outlined" className={classes.marginBottom}>
                          <CardHeader
                            avatar={<Assignment />}
                            title="INFO"
                            className={classes.primaryHeader}
                          />
                          <CardContent className={classes.cardContent}>
                            <Paper elevation={0} square>
                              <div className={classes.column}>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      required
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Name"
                                      name="name"
                                      size="small"
                                      value={values.name}
                                      onBlur={handleBlur}
                                      onChange={e => setFieldValue('name', e.target.value)}
                                      error={Boolean(touched.name && errors.name)}
                                      helperText={touched.name && errors.name}
                                    />
                                  </Grid>
                                </Grid>
                              </div>
                            </Paper>
                          </CardContent>
                        </Card>
                        <Card elevation={0} variant="outlined" className={classes.marginBottom}>
                          <CardHeader
                            avatar={<FormatListBulleted />}
                            title="TASKS"
                            className={classes.primaryHeader}
                          />
                          <CardContent className={classes.cardContent}>
                            <Paper elevation={0} square>
                              {currentTemplateTaskIndex === null && !loadingStartTemplates && (
                                <>
                                  <div className={classes.column}>
                                    <Grid container alignItems="center" spacing={1}>
                                      <Grid item xs={8} sm={6}>
                                        <SMBSelect
                                          name="copy"
                                          label="Copy from"
                                          variant="outlined"
                                          disabled={!startTemplates}
                                          id="copy"
                                          value={copyTaskSelectedTemplate}
                                          handleClear={() => setCopyTaskSelectedTemplate('')}
                                          onChange={e => {
                                            setCopyTaskSelectedTemplate(e.target.value as string);
                                          }}
                                        >
                                          {startTemplates &&
                                            startTemplates.map(
                                              (template: IStartSheetTemplate, index) => {
                                                return (
                                                  <MenuItem
                                                    key={template.startSheetTemplateId}
                                                    value={template.startSheetTemplateId}
                                                  >
                                                    {template.name}
                                                  </MenuItem>
                                                );
                                              }
                                            )}
                                        </SMBSelect>
                                      </Grid>
                                      <Grid item xs={4} sm={3}>
                                        <Button
                                          className="Copy Button"
                                          color="secondary"
                                          variant="contained"
                                          startIcon={<Add />}
                                          disabled={
                                            !copyTaskSelectedTemplate ||
                                            loadingDetail ||
                                            templateIsEmpty
                                          }
                                          onClick={() => {
                                            const copiedTasks = [...templateDetail.tasks];
                                            const noIdCopiedTasks = copiedTasks.map(task => {
                                              delete task.taskId;
                                              return task;
                                            });
                                            setFieldValue('tasks', [
                                              ...(values.tasks || []),
                                              ...noIdCopiedTasks
                                            ]);

                                            if (templateDetail.tasks.length === 0) {
                                              enqueueSnackbar(`There were no tasks to copy`, {
                                                variant: 'warning'
                                              });
                                            } else {
                                              enqueueSnackbar(
                                                `Tasks copied, be sure to save the template.`,
                                                {
                                                  variant: 'success'
                                                }
                                              );
                                            }
                                          }}
                                        >
                                          Copy
                                        </Button>
                                      </Grid>
                                      <Grid item xs={12} sm={3} className={classes.addTaskGridItem}>
                                        <Button
                                          variant="contained"
                                          color="secondary"
                                          startIcon={<Add />}
                                          onClick={() => {
                                            setFieldValue('tasks', [
                                              ...(values.tasks || []),
                                              {
                                                text: '',
                                                categories: [],
                                                assignedTo: '',
                                                subscribers: [],
                                                images: []
                                              }
                                            ]);
                                            setCurrentTemplateTaskIndex(
                                              values.tasks.length === 0 ? 0 : values.tasks.length
                                            );
                                          }}
                                        >
                                          Add Task
                                        </Button>
                                      </Grid>
                                    </Grid>
                                    <SortableList
                                      useDragHandle
                                      isMobile={isMobile}
                                      tasks={values.tasks}
                                      setCurrentTemplateTaskIndex={setCurrentTemplateTaskIndex}
                                      setFieldValue={setFieldValue}
                                      onSortEnd={onSortEnd}
                                      axis="y"
                                    />
                                  </div>
                                </>
                              )}
                              {(currentTemplateTaskIndex === 0 || currentTemplateTaskIndex > 0) &&
                                values.tasks[currentTemplateTaskIndex] && (
                                  <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                      <Field name={`tasks.${currentTemplateTaskIndex}.text`}>
                                        {({ field }) => {
                                          return (
                                            <TextField
                                              {...field}
                                              fullWidth
                                              variant="outlined"
                                              autoComplete="nope"
                                              label="Name"
                                              name={`tasks.${currentTemplateTaskIndex}.text`}
                                              value={values.tasks[currentTemplateTaskIndex].text}
                                              onBlur={handleBlur}
                                            />
                                          );
                                        }}
                                      </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Field name={`tasks.${currentTemplateTaskIndex}.categories`}>
                                        {({ field }) => {
                                          return (
                                            <FormControl fullWidth variant="outlined">
                                              <Autocomplete
                                                value={
                                                  values.tasks[currentTemplateTaskIndex].categories
                                                }
                                                multiple
                                                classes={{
                                                  paper: classes.paperBorder
                                                }}
                                                onChange={(
                                                  event,
                                                  newValue: IStartSheetTemplateCategory[]
                                                ) => {
                                                  if (newValue && newValue.length > 0) {
                                                    setFieldValue(
                                                      `tasks.${currentTemplateTaskIndex}.categories`,
                                                      newValue
                                                    );
                                                  } else {
                                                    setFieldValue(
                                                      `tasks.${currentTemplateTaskIndex}.categories`,
                                                      []
                                                    );
                                                  }
                                                }}
                                                id="categories"
                                                options={categories}
                                                disabled={isLoadingCategories}
                                                getOptionLabel={(
                                                  option: IStartSheetTemplateCategory
                                                ) => {
                                                  // Regular option
                                                  return option.name;
                                                }}
                                                renderInput={params => (
                                                  <TextField
                                                    {...params}
                                                    name={`tasks.${currentTemplateTaskIndex}.categories`}
                                                    autoComplete="none"
                                                    onBlur={handleBlur}
                                                    label="Categories"
                                                    variant="outlined"
                                                  />
                                                )}
                                              />
                                            </FormControl>
                                          );
                                        }}
                                      </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Field name={`tasks.${currentTemplateTaskIndex}.assignedTo`}>
                                        {({ field }) => {
                                          return (
                                            <FormControl fullWidth variant="outlined">
                                              <InputLabel
                                                id={`assignTo-${currentTemplateTaskIndex}`}
                                                className={classes.outlinedLabel}
                                              >
                                                Assign To
                                              </InputLabel>
                                              <Select
                                                {...field}
                                                name={`tasks.${currentTemplateTaskIndex}.assignedTo`}
                                                labelId={`tasks.${currentTemplateTaskIndex}.assignedTo`}
                                                id={`tasks.${currentTemplateTaskIndex}.assignedTo`}
                                                value={
                                                  values.tasks[currentTemplateTaskIndex]
                                                    .assignedTo || ''
                                                }
                                                onBlur={handleBlur}
                                              >
                                                {!isLoadingUsers &&
                                                  users.map((user: IDomainUser, index) => {
                                                    return (
                                                      <MenuItem
                                                        key={user.accountName}
                                                        value={user.accountName}
                                                      >
                                                        {user.accountName}
                                                      </MenuItem>
                                                    );
                                                  })}
                                              </Select>
                                            </FormControl>
                                          );
                                        }}
                                      </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Field name={`tasks.${currentTemplateTaskIndex}.subscribers`}>
                                        {({ field }) => {
                                          return (
                                            <FormControl fullWidth variant="outlined">
                                              <Autocomplete
                                                value={
                                                  values.tasks[currentTemplateTaskIndex].subscribers
                                                }
                                                multiple
                                                classes={{
                                                  paper: classes.paperBorder
                                                }}
                                                onChange={(event, newValue: string[]) => {
                                                  if (newValue && newValue.length > 0) {
                                                    setFieldValue(
                                                      `tasks.${currentTemplateTaskIndex}.subscribers`,
                                                      newValue
                                                    );
                                                  } else {
                                                    setFieldValue(
                                                      `tasks.${currentTemplateTaskIndex}.subscribers`,
                                                      []
                                                    );
                                                  }
                                                }}
                                                id="subscribers"
                                                options={users.map(user => user.accountName)}
                                                disabled={isLoadingUsers}
                                                getOptionLabel={(option: string) => {
                                                  return option;
                                                }}
                                                renderInput={params => (
                                                  <TextField
                                                    {...params}
                                                    name={`tasks.${currentTemplateTaskIndex}.subscribers`}
                                                    autoComplete="none"
                                                    onBlur={handleBlur}
                                                    label="Subscribers"
                                                    variant="outlined"
                                                  />
                                                )}
                                              />
                                            </FormControl>
                                          );
                                        }}
                                      </Field>
                                    </Grid>

                                    <Grid item xs={12}>
                                      <Field name={`tasks.${currentTemplateTaskIndex}.images`}>
                                        {({ field }) => {
                                          return (
                                            <FormControl fullWidth variant="outlined" size="small">
                                              <ImageUpload
                                                handleFileChange={val =>
                                                  setFieldValue(
                                                    `tasks.${currentTemplateTaskIndex}.images`,
                                                    [
                                                      ...(values.tasks[currentTemplateTaskIndex]
                                                        .images || []),
                                                      val
                                                    ]
                                                  )
                                                }
                                                image={
                                                  (values.tasks[currentTemplateTaskIndex].images &&
                                                    values.tasks[currentTemplateTaskIndex].images[
                                                      values.tasks[currentTemplateTaskIndex].images
                                                        .length - 1
                                                    ]) ||
                                                  ''
                                                }
                                              />
                                            </FormControl>
                                          );
                                        }}
                                      </Field>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="flex-end"
                                      >
                                        <ButtonGroup>
                                          <Button
                                            color="secondary"
                                            variant="contained"
                                            startIcon={<Add />}
                                            disabled={!values.tasks[currentTemplateTaskIndex].text}
                                            onClick={() => {
                                              setCurrentTemplateTaskIndex(null);
                                              enqueueSnackbar(
                                                `Task Added, be sure to save the template.`,
                                                {
                                                  variant: 'success'
                                                }
                                              );
                                            }}
                                          >
                                            Done
                                          </Button>
                                          <Button
                                            variant="contained"
                                            startIcon={<Close />}
                                            color="inherit"
                                            onClick={() => {
                                              if (!values.tasks[currentTemplateTaskIndex].taskId) {
                                                setCurrentTemplateTaskIndex(null);
                                                const newValues = [...values.tasks];
                                                newValues.pop();
                                                setFieldValue(`tasks`, newValues);
                                              } else {
                                                setCurrentTemplateTaskIndex(null);
                                              }
                                            }}
                                          >
                                            Cancel Task
                                          </Button>
                                        </ButtonGroup>
                                      </Box>
                                    </Grid>
                                  </Grid>
                                )}
                            </Paper>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </div>
                  {currentTemplateTaskIndex === null && (
                    <ButtonGroup>
                      <Button
                        disabled={!dirty || isSubmitting || !isValid}
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        startIcon={<Close />}
                        onClick={() => {
                          if (!deepEqual(initialValues, values)) {
                            const result = window.confirm(
                              'You have unsaved changes, are you sure you want to exit?'
                            );
                            if (result) {
                              resetForm();
                              setCopyTaskSelectedTemplate('');
                              setTemplateIsEmpty(false);
                              setCurrentTemplateTaskIndex(null);
                              onClose();
                            } else {
                              return;
                            }
                          } else {
                            setCopyTaskSelectedTemplate('');
                            setTemplateIsEmpty(false);
                            setCurrentTemplateTaskIndex(null);
                            onClose();
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    </ButtonGroup>
                  )}
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    [theme.breakpoints.down('md')]: {
      marginTop: 12,
      textAlign: 'right'
    }
  },
  title: {
    textTransform: 'uppercase'
  },
  primaryHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(1)
  },
  marginBottom: {
    marginBottom: theme.spacing(1)
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  copyTasksButton: { marginLeft: theme.spacing(0.5) },
  column: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    '& > div:not(:first-of-type)': {
      marginTop: theme.spacing(1)
    }
  },
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  content: {
    marginTop: theme.spacing(1)
  },
  cardContent: {
    paddingTop: 0
  },
  paperBorder: {
    border: `1px solid ${theme.palette.grey[300]}`
  },
  addTaskButton: {
    marginTop: theme.spacing(1)
  },
  editButton: {
    textAlign: 'right',
    fontSize: 13
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  addTaskGridItem: {
    [theme.breakpoints.up('sm')]: {
      textAlign: 'end'
    },
    color: theme.palette.error.main,
    fontSize: 13
  },
  taskCard: {
    width: '100%',
    marginTop: 8,
    marginBottom: 8,
    position: 'relative',
    zIndex: 1500
  },
  taskCardContent: {
    padding: `12px !important`
  },
  bold: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    fontSize: 12
  },
  taskValue: {
    fontSize: 15
  },
  dragIcon: {
    position: 'absolute',
    top: 25,
    right: 25,
    background: 'white',
    fill: theme.palette.grey[600],
    fontSize: '2rem',
    cursor: 'grab'
  }
}));
